import { useEffect, useId, useRef, useState } from 'react';
import type { PlaceAddressDetails, PlaceSuggestion } from '@peles/shared';
import { fetchAddressDetails, fetchAddressSuggestions } from '../../api/places';

export type AddressFields = PlaceAddressDetails;

interface Props {
  id?: string;
  value: string;
  onChange: (value: string) => void;
  onAddressSelect?: (fields: AddressFields) => void;
  disabled?: boolean;
  placeholder?: string;
  className?: string;
}

const MIN_QUERY_LENGTH = 3;
const DEBOUNCE_MS = 300;

// Places billing groups an autocomplete burst + the follow-up details call
// into one session via this token; a new token starts after each selection.
function newSessionToken(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return `${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

/**
 * Address input with realtime suggestions from the Google Places database,
 * proxied through our backend (`/api/v1/places`) — no browser autofill, no
 * client-side Maps SDK, no frontend API key. Degrades to a plain text input
 * when the backend key is unconfigured or the API is unreachable.
 */
export function GoogleAddressAutocomplete({
  id,
  value,
  onChange,
  onAddressSelect,
  disabled,
  placeholder = 'Optional',
  className,
}: Props) {
  const [suggestions, setSuggestions] = useState<PlaceSuggestion[]>([]);
  const [isOpen, setIsOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const blurRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const requestSeqRef = useRef(0);
  const sessionTokenRef = useRef<string | null>(null);
  const generatedId = useId();
  const listboxId = `${id ?? generatedId}-suggestions`;

  useEffect(() => {
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
      if (blurRef.current) clearTimeout(blurRef.current);
      // Invalidate any in-flight suggestion request
      requestSeqRef.current++;
    };
  }, []);

  function closeDropdown() {
    setIsOpen(false);
    setActiveIndex(-1);
  }

  function handleInput(next: string) {
    onChange(next);
    // Every keystroke invalidates in-flight suggestion/details responses so a
    // late response can never clobber what the user typed after it was sent
    requestSeqRef.current++;
    if (debounceRef.current) clearTimeout(debounceRef.current);
    const query = next.trim();
    if (query.length < MIN_QUERY_LENGTH) {
      setSuggestions([]);
      closeDropdown();
      return;
    }
    debounceRef.current = setTimeout(() => {
      if (!sessionTokenRef.current) sessionTokenRef.current = newSessionToken();
      const seq = ++requestSeqRef.current;
      fetchAddressSuggestions(query, sessionTokenRef.current).then((results) => {
        if (seq !== requestSeqRef.current) return;
        setSuggestions(results);
        setIsOpen(results.length > 0);
        setActiveIndex(-1);
      });
    }, DEBOUNCE_MS);
  }

  function handleSelect(suggestion: PlaceSuggestion) {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    const seq = ++requestSeqRef.current;
    setSuggestions([]);
    closeDropdown();
    const sessionToken = sessionTokenRef.current ?? undefined;
    sessionTokenRef.current = null;
    // Show the picked suggestion immediately; refine once details resolve
    onChange(suggestion.description);
    fetchAddressDetails(suggestion.placeId, sessionToken).then((details) => {
      // Bail if the user typed again while details were in flight
      if (seq !== requestSeqRef.current || !details) return;
      onChange(details.address || details.formattedAddress);
      onAddressSelect?.(details);
    });
  }

  function handleKeyDown(e: React.KeyboardEvent<HTMLInputElement>) {
    if (!isOpen || suggestions.length === 0) return;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActiveIndex((i) => (i + 1) % suggestions.length);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveIndex((i) => (i <= 0 ? suggestions.length - 1 : i - 1));
    } else if (e.key === 'Enter') {
      if (activeIndex >= 0 && activeIndex < suggestions.length) {
        e.preventDefault();
        handleSelect(suggestions[activeIndex]);
      }
    } else if (e.key === 'Escape') {
      closeDropdown();
    }
  }

  return (
    <div className="relative">
      <input
        id={id}
        type="text"
        value={value}
        onChange={(e) => handleInput(e.target.value)}
        onKeyDown={handleKeyDown}
        onBlur={() => {
          blurRef.current = setTimeout(() => closeDropdown(), 150);
        }}
        onFocus={() => {
          if (blurRef.current) clearTimeout(blurRef.current);
          if (suggestions.length > 0 && value.trim().length >= MIN_QUERY_LENGTH) setIsOpen(true);
        }}
        disabled={disabled}
        placeholder={placeholder}
        className={className}
        role="combobox"
        aria-expanded={isOpen}
        aria-autocomplete="list"
        aria-controls={listboxId}
        // Chrome ignores autocomplete="off" on address-looking fields; the
        // "new-password" hint is the reliable way to keep browser autofill
        // from covering our Places suggestion dropdown.
        autoComplete="new-password"
      />
      {isOpen && (
        <ul
          id={listboxId}
          role="listbox"
          className="absolute z-20 mt-1 w-full bg-white border border-gray-200 rounded-md shadow-lg max-h-60 overflow-auto"
        >
          {suggestions.map((suggestion, index) => (
            <li
              key={suggestion.placeId}
              role="option"
              aria-selected={index === activeIndex}
              className={`px-3 py-2 cursor-pointer text-sm text-gray-900 ${
                index === activeIndex ? 'bg-primary-50' : 'hover:bg-gray-50'
              }`}
              onMouseDown={(e) => {
                e.preventDefault();
                handleSelect(suggestion);
              }}
              onMouseEnter={() => setActiveIndex(index)}
            >
              <span className="font-medium">{suggestion.mainText}</span>
              {suggestion.secondaryText && (
                <span className="text-xs text-gray-500 ml-2">{suggestion.secondaryText}</span>
              )}
            </li>
          ))}
          <li className="px-3 py-1 text-right text-[10px] text-gray-400 border-t border-gray-100 select-none">
            Powered by Google
          </li>
        </ul>
      )}
    </div>
  );
}
